/** @file Timeline: clip list, header, transport, ruler, events and key rows (key context timeline). */
import type { ReactElement } from 'react';
import { useMemo, useRef } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { usePanelKeys } from '../tree/usePanelKeys';
import { ClipList } from './ClipList';
import { EventRow } from './EventRow';
import { FrameRuler } from './FrameRuler';
import { KeyRows } from './KeyRows';
import type { PoseClipboard } from './poseClipboard';
import { timelineBindings } from './timelineBindings';
import { timelineHandlers } from './timelineHandlers';
import { TimelineHeader } from './TimelineHeader';
import { TransportBar } from './TransportBar';

/** The note shown over the timeline while Quads draws the rest pose instead of the clip. */
const QUADS_NOTE = 'Quads: rest pose (turn Quads off to see the clip)';

/** Reads the playhead clip from the store; one pose clipboard per mounted timeline; dimmed with a note while Quads is on. */
export function Timeline(): ReactElement {
  const store = useEditorStore();
  const quads = useEditor((s) => s.camera.quads);
  const clipboard = useRef<PoseClipboard['current']>(null);
  const handlers = useMemo(() => timelineHandlers(store, clipboard), [store]);
  const keyProps = usePanelKeys('timeline', timelineBindings, handlers);
  return (
    <div data-panel="timeline" {...keyProps} data-quads={quads ? 'rest' : undefined}
      style={{ display: 'flex', height: '100%', fontSize: 12, outline: 'none', fontFamily: 'var(--font-sans, system-ui, sans-serif)', position: 'relative', opacity: quads ? 0.55 : 1 }}>
      {quads ? <div role="note" style={{ position: 'absolute', top: 4, right: 8, fontSize: 11, color: 'var(--accent, #e3a93c)', pointerEvents: 'none' }}>{QUADS_NOTE}</div> : null}
      <ClipList />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TimelineHeader />
        <TransportBar handlers={handlers} />
        <div style={{ flex: 1, overflow: 'auto' }}>
          <FrameRuler />
          <EventRow />
          <KeyRows clipboard={clipboard} />
        </div>
      </div>
    </div>
  );
}
